import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'sonner'
import {
  MapPin, Euro, ChevronRight, AlertCircle, Calendar, User,
  Send, MessageSquare, Lock, Paperclip, FileText, Image as ImageIcon, Eye,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import PublicPageHeader from '../components/PublicPageHeader'
import PublicFooter from '../components/PublicFooter'
import { useAuth } from '../context/AuthContext'
import { messagingPaths } from '../utils/messagingPaths'
import { postsService } from '../services/api'

import API from '../constants/api'
// API base via getApiBase()

const urgencyLabels = {
  normale: { label: 'Normal', color: 'bg-slate-100 text-slate-700' },
  urgente: { label: 'Urgent', color: 'bg-amber-100 text-amber-700' },
  tres_urgente: { label: 'Très urgent', color: 'bg-red-100 text-red-700' },
}

const parseSalaryRange = (salaryRange) => {
  if (!salaryRange) return {}
  try {
    const parsed = JSON.parse(salaryRange)
    if (typeof parsed === 'object' && parsed !== null) return parsed
  } catch {
    return { label: salaryRange }
  }
  return {}
}

const formatBudget = (salaryRange) => {
  const budget = parseSalaryRange(salaryRange)
  if (budget.label) return budget.label
  if (budget.min != null && budget.max != null) {
    return `${Number(budget.min).toLocaleString('fr-FR')} € – ${Number(budget.max).toLocaleString('fr-FR')} €`
  }
  if (budget.max != null) return `Jusqu'à ${Number(budget.max).toLocaleString('fr-FR')} €`
  if (budget.min != null) return `À partir de ${Number(budget.min).toLocaleString('fr-FR')} €`
  return null
}

const parseAuthorFirstName = (name = '') => name.trim().split(/\s+/)[0] || ''

const normalizeAttachments = (attachments) => {
  if (!Array.isArray(attachments)) return []
  return attachments.filter((item) => item && typeof item.url === 'string')
}

const isPdfAttachment = (attachment) =>
  attachment.mimeType === 'application/pdf'
  || attachment.name?.toLowerCase().endsWith('.pdf')

export default function RequestDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, loading: authLoading, isAuthenticated, getAuthToken, isProView } = useAuth()
  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accessDenied, setAccessDenied] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [responding, setResponding] = useState(false)

  useEffect(() => {
    if (authLoading) return

    const fetchRequest = async () => {
      setLoading(true)
      setAccessDenied(false)
      setNotFound(false)

      const authToken = getAuthToken()
      if (!authToken) {
        setAccessDenied(true)
        setLoading(false)
        return
      }

      try {
        const response = await axios.get(`${API}/posts/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        const requestData = response.data?.data ?? response.data
        if (!requestData?.id) {
          setNotFound(true)
          return
        }
        setRequest(requestData)

        const isAuthor =
          user?.id === requestData.authorUserId || user?.id === requestData.author?.id
        if (!isAuthor) {
          postsService.recordView(id).catch(() => {})
        }
      } catch (error) {
        const status = error.response?.status
        if (status === 403 || status === 401) {
          setAccessDenied(true)
        } else if (status === 404) {
          setNotFound(true)
        } else {
          console.error('Error fetching request:', error)
          toast.error('Erreur lors du chargement de la demande')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchRequest()
  }, [id, authLoading, getAuthToken, user?.id])

  const handleRespond = async () => {
    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter')
      navigate('/connexion', { state: { from: `/demandes/${id}` } })
      return
    }

    if (!isProView()) {
      toast.error('Passez en mode Professionnel pour répondre aux demandes')
      return
    }

    const authToken = getAuthToken()
    if (!authToken) {
      toast.error('Session expirée. Reconnectez-vous.')
      navigate('/connexion', { state: { from: `/demandes/${id}` } })
      return
    }

    setResponding(true)
    try {
      const response = await axios.post(
        `${API}/applications`,
        { postId: id, message: 'Bonjour, je suis intéressé par votre demande.' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      const chatId = response.data?.data?.chatId ?? response.data?.chatId
      toast.success('Candidature envoyée !')
      if (chatId) {
        navigate(messagingPaths.chat({ chatId, postId: id }))
      } else {
        navigate('/tableau-de-bord/mes-candidatures')
      }
    } catch (error) {
      const msg =
        error.response?.data?.error?.message ??
        error.response?.data?.message ??
        'Erreur lors de l\'envoi'
      toast.error(msg)
    } finally {
      setResponding(false)
    }
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--primary))]" />
      </div>
    )
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <PublicPageHeader activePath="demandes" />
        <div className="flex-1 container-clickpro py-16">
          <div className="max-w-lg mx-auto bg-white rounded-2xl border border-slate-200 p-10 text-center">
            <div className="w-16 h-16 bg-[hsl(var(--primary)/0.08)] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[hsl(var(--primary))]" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Accès réservé</h1>
            <p className="text-slate-600 mb-6">
              {!isAuthenticated
                ? 'Connectez-vous avec un compte Pro validé pour consulter cette demande.'
                : 'Cette demande est accessible aux comptes Pro validés.'}
            </p>
            <div className="flex gap-3 justify-center">
              {!isAuthenticated ? (
                <>
                  <Link to="/connexion" state={{ from: `/demandes/${id}` }}>
                    <Button variant="outline">Se connecter</Button>
                  </Link>
                  <Link to="/inscription">
                    <Button className="btn-clickpro-primary">Devenir partenaire</Button>
                  </Link>
                </>
              ) : (
                <Link to="/tableau-de-bord/profil">
                  <Button className="btn-clickpro-primary">Demander l&apos;accès Pro</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    )
  }

  if (notFound || !request) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <PublicPageHeader activePath="demandes" />
        <div className="flex-1 container-clickpro py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Demande non trouvée</h1>
          <Link to="/demandes">
            <Button className="btn-clickpro-primary">Retour aux demandes</Button>
          </Link>
        </div>
        <PublicFooter />
      </div>
    )
  }

  const budget = parseSalaryRange(request.salaryRange)
  const budgetLabel = formatBudget(request.salaryRange)
  const urgency = budget.urgence ? urgencyLabels[budget.urgence] : null
  const requestAttachments = normalizeAttachments(request.attachments)
  const applicationsCount = request._count?.applications ?? 0
  const viewCount = request.viewCount ?? 0
  const isOwner = user?.id === request.authorUserId || user?.id === request.author?.id

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PublicPageHeader activePath="demandes" />

      <div className="bg-white border-b border-slate-100">
        <div className="container-clickpro py-6">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <a href="https://clicketpro.fr" className="hover:text-[hsl(var(--primary))]">Accueil</a>
            <ChevronRight className="w-4 h-4" />
            <Link to="/demandes" className="hover:text-[hsl(var(--primary))]">Demandes</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 font-medium truncate max-w-[240px] sm:max-w-md">
              {request.title}
            </span>
          </div>
        </div>
      </div>

      <div className="container-clickpro py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <article className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
              <div className="flex items-center gap-2 flex-wrap mb-5">
                {request.category && (
                  <Badge
                    className="font-medium"
                    style={{
                      backgroundColor: request.category.color
                        ? `${request.category.color}20`
                        : 'hsl(var(--primary)/0.08)',
                      color: request.category.color ?? 'hsl(var(--primary-hover))',
                    }}
                  >
                    {request.category.name}
                  </Badge>
                )}
                {request.status === 'PUBLISHED' && (
                  <Badge className="bg-green-50 text-green-700 font-medium text-xs">Publié</Badge>
                )}
                {urgency && (
                  <Badge className={urgency.color}>
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {urgency.label}
                  </Badge>
                )}
              </div>

              <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4 leading-tight">
                {request.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6 pb-6 border-b border-slate-100">
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  {request.locationCity}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  Publié le {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                </span>
                {request.author?.name && (
                  <span className="flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    {parseAuthorFirstName(request.author.name)}
                  </span>
                )}
              </div>

              <div>
                <h2 className="font-semibold text-slate-900 mb-3">Description du projet</h2>
                <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">
                  {request.description?.trim() ? request.description : 'Aucune description fournie.'}
                </p>
              </div>

              {requestAttachments.length > 0 && (
                <div className="border-t border-slate-100 pt-6 mt-6">
                  <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Pièces jointes
                  </h2>
                  <div className="space-y-2">
                    {requestAttachments.map((attachment) => (
                      <a
                        key={`${attachment.url}-${attachment.name}`}
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.08)] flex items-center justify-center shrink-0">
                          {isPdfAttachment(attachment) ? (
                            <FileText className="w-5 h-5 text-[hsl(var(--primary))]" />
                          ) : (
                            <ImageIcon className="w-5 h-5 text-[hsl(var(--primary))]" />
                          )}
                        </div>
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {attachment.name || 'Pièce jointe'}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {budget.dateSouhaitee && (
                <div className="border-t border-slate-100 pt-6 mt-6">
                  <h2 className="font-semibold text-slate-900 mb-3">Date souhaitée</h2>
                  <p className="text-slate-600">
                    {new Date(budget.dateSouhaitee).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
            </article>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Budget estimé</h3>
              {budgetLabel ? (
                <div className="flex items-start gap-2">
                  <Euro className="w-5 h-5 text-[hsl(var(--primary))] mt-1 shrink-0" />
                  <p className="text-xl md:text-2xl font-bold text-slate-900 leading-snug">{budgetLabel}</p>
                </div>
              ) : (
                <p className="text-slate-500">Budget non précisé</p>
              )}
            </div>

            {!isOwner && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Répondre à cette demande</h3>
                <p className="text-slate-600 mb-5 text-sm leading-relaxed">
                  {isProView()
                    ? 'Proposez vos services et démarrez une conversation avec le client.'
                    : 'Passez en mode Professionnel pour répondre à cette demande.'}
                </p>
                {isProView() ? (
                  <Button
                    className="w-full btn-clickpro-primary"
                    onClick={handleRespond}
                    disabled={responding}
                    data-testid="respond-btn"
                  >
                    {responding ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Répondre à cette demande
                      </>
                    )}
                  </Button>
                ) : isAuthenticated ? (
                  <Link to="/tableau-de-bord/profil">
                    <Button className="w-full btn-clickpro-primary">Activer l&apos;espace Pro</Button>
                  </Link>
                ) : (
                  <Link to="/connexion" state={{ from: `/demandes/${id}` }}>
                    <Button className="w-full btn-clickpro-primary">Se connecter pour répondre</Button>
                  </Link>
                )}
              </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Informations</h3>
              <dl className="space-y-3 text-sm">
                {request.category?.name && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Catégorie</dt>
                    <dd className="text-slate-900 font-medium text-right">{request.category.name}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Localisation</dt>
                  <dd className="text-slate-900 font-medium text-right">{request.locationCity}</dd>
                </div>
                {urgency && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Urgence</dt>
                    <dd className="text-slate-900 font-medium text-right">{urgency.label}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500 flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5" />
                    Réponses
                  </dt>
                  <dd className="text-slate-900 font-medium text-right">{applicationsCount}</dd>
                </div>
                {isOwner && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500 flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" />
                      Vues
                    </dt>
                    <dd className="text-slate-900 font-medium text-right">{viewCount}</dd>
                  </div>
                )}
              </dl>
            </div>
          </aside>
        </div>
      </div>
      <PublicFooter />
    </div>
  )
}
